// Bailey V1.5 - Dashboard Agent Shadow WhatsApp
// Interface centrée sur l'activité avec validation humaine
// Modèle mental : "Je vois tout ce que Bailey a vu, compris et fait. Quand il doute, je valide."

import { hasIntegrationPennylane } from "../../../utils/tenantProfile";
import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from "../../../components/ui/dialog";
import { 
  Bot, MessageSquare, Mail, FolderOpen, CheckCircle2, Clock, 
  ChevronRight, Plus, Settings2, Smartphone, AlertCircle,
  Eye, Send, Archive, XCircle, Loader2, ArrowLeft, Trash2,
  Image, FileText, Video, Mic, Check, X, CircleDot, RefreshCw
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// =============================================================================
// COMPOSANTS RÉUTILISABLES
// =============================================================================

// Icône selon le type de message
const MessageTypeIcon = ({ type }) => {
  switch (type) {
    case "image":
      return <Image className="w-4 h-4 text-blue-400" />;
    case "document":
      return <FileText className="w-4 h-4 text-amber-400" />;
    case "video":
      return <Video className="w-4 h-4 text-purple-400" />;
    case "audio":
      return <Mic className="w-4 h-4 text-green-400" />;
    default:
      return <MessageSquare className="w-4 h-4 text-zinc-400" />;
  }
};

// Badge de statut d'activité avec couleur
const ActivityStatusBadge = ({ status }) => {
  const configs = {
    auto_executed: { 
      className: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
      icon: <CheckCircle2 className="w-3 h-3 mr-1" />,
      label: "Exécuté"
    },
    action_proposed: { 
      className: "bg-violet-500/20 text-violet-400 border-violet-500/30",
      icon: <CircleDot className="w-3 h-3 mr-1" />,
      label: "À valider"
    },
    validated: { 
      className: "bg-blue-500/20 text-blue-400 border-blue-500/30",
      icon: <Check className="w-3 h-3 mr-1" />,
      label: "Validé"
    },
    executed: { 
      className: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
      icon: <CheckCircle2 className="w-3 h-3 mr-1" />,
      label: "Exécuté"
    },
    ignored: { 
      className: "bg-zinc-500/20 text-zinc-400 border-zinc-500/30",
      icon: <XCircle className="w-3 h-3 mr-1" />,
      label: "Ignoré"
    },
    analysed: { 
      className: "bg-zinc-500/20 text-zinc-400 border-zinc-500/30",
      icon: <Eye className="w-3 h-3 mr-1" />,
      label: "Analysé"
    }
  };
  
  const config = configs[status] || configs.analysed;
  
  return (
    <Badge className={`${config.className} text-xs flex items-center`}>
      {config.icon}
      {config.label}
    </Badge>
  );
};

// Formatage du temps relatif
const timeAgo = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const now = new Date();
  const diff = Math.floor((now - date) / 1000);
  
  if (diff < 60) return "À l'instant";
  if (diff < 3600) return `Il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `Il y a ${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `Il y a ${Math.floor(diff / 86400)}j`;
  return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

// Formatage de l'heure seule
const formatTime = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
};

// =============================================================================
// COMPOSANT CARTE D'ACTIVITÉ
// =============================================================================

const ActivityCard = ({ activity, onValidate, onIgnore, onArchive, onSendEmail, onDelete, isValidating }) => {
  const isPending = activity.status === "action_proposed";
  
  return (
    <div 
      data-testid={`activity-card-${activity.id}`}
      className={`p-4 rounded-lg border transition-all group ${
        isPending 
          ? "bg-violet-500/5 border-violet-500/30 hover:border-violet-500/50" 
          : "bg-zinc-900/50 border-zinc-800 hover:border-zinc-700"
      }`}
    >
      {/* En-tête : Heure + Groupe + Actions */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-sm text-zinc-500 font-mono">
            {formatTime(activity.processed_at)}
          </span>
          <span className="text-white font-medium">{activity.group_name}</span>
        </div>
        <div className="flex items-center gap-2">
          <ActivityStatusBadge status={activity.status} />
          {/* Boutons Archiver/Supprimer - visibles au hover */}
          <div className="opacity-0 group-hover:opacity-100 flex items-center gap-1 transition-opacity">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onArchive(activity.id)}
              className="h-7 w-7 p-0 text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800"
              title="Archiver"
            >
              <Archive className="w-3.5 h-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete(activity.id)}
              className="h-7 w-7 p-0 text-zinc-500 hover:text-red-400 hover:bg-zinc-800"
              title="Supprimer"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </div>
      
      {/* Contenu : Type + Expéditeur */}
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-zinc-800">
          <MessageTypeIcon type={activity.message_type} />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm text-zinc-300">
              {activity.message_type === "text" ? "Message" : activity.message_type.charAt(0).toUpperCase() + activity.message_type.slice(1)} de {activity.sender_name}
            </span>
          </div>
          
          {/* Décision Bailey */}
          <p className="text-sm text-white">
            {activity.decision || activity.detected_type || "Message traité"}
          </p>
          
          {/* Données extraites */}
          {(activity.extracted_amount || activity.extracted_vendor) && (
            <div className="flex items-center gap-2 mt-1">
              {activity.extracted_amount && (
                <span className="text-xs text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded">
                  {activity.extracted_amount}
                </span>
              )}
              {activity.extracted_vendor && (
                <span className="text-xs text-zinc-400">
                  {activity.extracted_vendor}
                </span>
              )}
            </div>
          )}
          
          {/* Indicateurs Email + Zoho - pour les activités exécutées */}
          {activity.status !== "action_proposed" && (
            <div className="flex items-center gap-3 mt-2">
              {/* Statut Email */}
              {activity.email_sent ? (
                <div className="flex items-center gap-1 text-xs text-emerald-400">
                  <Mail className="w-3 h-3" />
                  <CheckCircle2 className="w-3 h-3" />
                  Email envoyé
                </div>
              ) : activity.has_media && activity.group_type === "depense" ? (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => onSendEmail && onSendEmail(activity.id)}
                  className="h-6 px-2 text-xs text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"
                >
                  <Mail className="w-3 h-3 mr-1" />
                  Envoyer email
                </Button>
              ) : null}
              
              {/* Statut Zoho */}
              {activity.zoho_path ? (
                <div className="flex items-center gap-1 text-xs text-blue-400">
                  <Archive className="w-3 h-3" />
                  <CheckCircle2 className="w-3 h-3" />
                  Archivé Zoho
                </div>
              ) : activity.has_media ? (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => onArchive(activity.id)}
                  className="h-6 px-2 text-xs text-zinc-400 hover:text-zinc-300 hover:bg-zinc-700"
                >
                  <Archive className="w-3 h-3 mr-1" />
                  Archiver Zoho
                </Button>
              ) : null}
            </div>
          )}
        </div>
      </div>
      
      {/* Boutons de validation si en attente */}
      {isPending && (
        <div className="mt-4 pt-3 border-t border-zinc-800">
          <p className="text-sm text-zinc-400 mb-3">
            Que voulez-vous faire ?
            {activity.proposed_target && (
              <span className="text-zinc-500 ml-1">
                → {activity.proposed_target}
              </span>
            )}
          </p>
          <div className="flex gap-2">
            <Button
              data-testid={`validate-btn-${activity.id}`}
              size="sm"
              onClick={() => onValidate(activity.id)}
              disabled={isValidating}
              className="bg-violet-600 hover:bg-violet-700 flex-1"
            >
              {isValidating ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  {activity.proposed_action === "send_email" ? "Envoyer par email" : "Valider"}
                </>
              )}
            </Button>
            <Button
              data-testid={`ignore-btn-${activity.id}`}
              size="sm"
              variant="outline"
              onClick={() => onIgnore(activity.id)}
              disabled={isValidating}
              className="border-zinc-700 hover:bg-zinc-800"
            >
              <X className="w-4 h-4 mr-2" />
              Ignorer
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// COMPOSANT PRINCIPAL
// =============================================================================

const BaileyDashboard = ({ tenantId = "tef" }) => {
  // États
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    messages_seen: 0,
    actions_auto: 0,
    actions_executed: 0,
    pending_validation: 0,
    ignored: 0
  });
  const [activities, setActivities] = useState([]);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [observedGroups, setObservedGroups] = useState([]);
  const [whatsappStatus, setWhatsappStatus] = useState({ connected: false });
  const [validatingId, setValidatingId] = useState(null);
  const [reconnecting, setReconnecting] = useState(false);
  
  // Vue active : "activity" ou "config"
  const [activeView, setActiveView] = useState("activity");
  
  // Dialogs
  const [showAddGroup, setShowAddGroup] = useState(false);
  const [showConfigGroup, setShowConfigGroup] = useState(null);
  const [groupConfig, setGroupConfig] = useState({ behavior: "depense", email: "" });

  // =============================================================================
  // CHARGEMENT DES DONNÉES
  // =============================================================================

  const fetchData = useCallback(async () => {
    try {
      const tenantParam = tenantId ? `?tenant_id=${tenantId}` : '';
      const tenantParamAnd = tenantId ? `&tenant_id=${tenantId}` : '';

      // Stats d'activité
      const statsRes = await fetch(`${API_URL}/api/bailey/activity/stats${tenantParam}`);
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }
      
      // Journal d'activité
      const activityRes = await fetch(`${API_URL}/api/bailey/activity?limit=30${tenantParamAnd}`);
      if (activityRes.ok) {
        const data = await activityRes.json();
        setActivities(data.activities || []);
      }
      
      // Statut WhatsApp
      const whatsappRes = await fetch(`${API_URL}/api/bailey/health${tenantParam}`);
      if (whatsappRes.ok) {
        const health = await whatsappRes.json();
        setWhatsappStatus({
          connected: health.whatsapp_connected,
          phone: health.whatsapp_phone || "",
          name: health.whatsapp_name || ""
        });
      }
      
      // Groupes WhatsApp disponibles (via Baileys)
      const availRes = await fetch(`${API_URL}/api/whatsapp/baileys/groups${tenantParam}`);
      if (availRes.ok) {
        const data = await availRes.json();
        setAvailableGroups(data.groups || []);
      }
      
      // Groupes observés (configurés)
      const groupsRes = await fetch(`${API_URL}/api/bailey/groups${tenantParam}`);
      if (groupsRes.ok) {
        const data = await groupsRes.json();
        const groups = data.groups || data || [];
        setObservedGroups(groups.filter(g => g.group_type !== "non_configure"));
      }
      
    } catch (error) {
      console.error("Erreur chargement Bailey:", error);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 15000); // Rafraîchir toutes les 15 secondes
    return () => clearInterval(interval);
  }, [fetchData]);

  // =============================================================================
  // ACTIONS
  // =============================================================================

  const handleValidate = async (activityId) => {
    setValidatingId(activityId);
    try {
      const res = await fetch(`${API_URL}/api/bailey/activity/${activityId}/validate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ choice: "approve" })
      });
      
      if (res.ok) {
        fetchData(); // Rafraîchir les données
      }
    } catch (error) {
      console.error("Erreur validation:", error);
    } finally {
      setValidatingId(null);
    }
  };

  const handleIgnore = async (activityId) => {
    setValidatingId(activityId);
    try {
      const res = await fetch(`${API_URL}/api/bailey/activity/${activityId}/validate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ choice: "ignore" })
      });
      
      if (res.ok) {
        fetchData();
      }
    } catch (error) {
      console.error("Erreur ignore:", error);
    } finally {
      setValidatingId(null);
    }
  };

  const handleArchive = async (activityId) => {
    try {
      const res = await fetch(`${API_URL}/api/bailey/activity/${activityId}/archive`, {
        method: "POST"
      });
      
      if (res.ok) {
        // Retirer de la liste locale immédiatement
        setActivities(prev => prev.filter(a => a.id !== activityId));
      }
    } catch (error) {
      console.error("Erreur archivage:", error);
    }
  };

  const handleDelete = async (activityId) => {
    if (!window.confirm("Supprimer définitivement cette entrée ?")) return;
    
    try {
      const res = await fetch(`${API_URL}/api/bailey/activity/${activityId}`, {
        method: "DELETE"
      });
      
      if (res.ok) {
        // Retirer de la liste locale immédiatement
        setActivities(prev => prev.filter(a => a.id !== activityId));
      }
    } catch (error) {
      console.error("Erreur suppression:", error);
    }
  };

  const handleSendEmail = async (activityId) => {
    setValidatingId(activityId);
    try {
      const res = await fetch(`${API_URL}/api/bailey/activity/${activityId}/send-email`, {
        method: "POST"
      });
      
      if (res.ok) {
        // Rafraîchir les données pour voir le statut mis à jour
        fetchData();
      } else {
        const error = await res.json();
        console.error("Erreur envoi email:", error.detail);
        alert(`Erreur: ${error.detail || "Impossible d'envoyer l'email"}`);
      }
    } catch (error) {
      console.error("Erreur envoi email:", error);
    } finally {
      setValidatingId(null);
    }
  };

  const handleReconnectWhatsApp = async () => {
    setReconnecting(true);
    try {
      const res = await fetch(`${API_URL}/api/bailey/whatsapp/reconnect`, {
        method: "POST"
      });
      
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          // Mettre à jour le statut
          setWhatsappStatus({
            connected: data.connected,
            groups_count: data.groups_count
          });
          // Rafraîchir toutes les données
          fetchData();
        }
      }
    } catch (error) {
      console.error("Erreur reconnexion:", error);
    } finally {
      setReconnecting(false);
    }
  };

  const addGroupToObserve = async (whatsappGroup) => {
    try {
      const res = await fetch(`${API_URL}/api/bailey/groups/configure`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          group_id: whatsappGroup.id,
          group_name: whatsappGroup.name,
          group_type: "non_configure",
          tenant_id: tenantId,
          enabled: true
        })
      });
      
      if (res.ok) {
        setShowAddGroup(false);
        setShowConfigGroup({
          group_id: whatsappGroup.id,
          group_name: whatsappGroup.name,
          isNew: true
        });
        fetchData();
      } else {
        console.error("Erreur API:", await res.text());
      }
    } catch (error) {
      console.error("Erreur ajout groupe:", error);
    }
  };

  const saveGroupConfig = async () => {
    if (!showConfigGroup) return;
    
    try {
      const res = await fetch(`${API_URL}/api/bailey/groups/${showConfigGroup.group_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          group_type: groupConfig.behavior,
          expense_email: groupConfig.behavior === "depense" ? groupConfig.email : null,
          auto_process_expenses: groupConfig.behavior === "depense",
          detect_planning: groupConfig.behavior === "planning",
          enabled: groupConfig.behavior !== "ignorer"
        })
      });
      
      if (res.ok) {
        setShowConfigGroup(null);
        setGroupConfig({ behavior: "depense", email: "" });
        fetchData();
      }
    } catch (error) {
      console.error("Erreur config groupe:", error);
    }
  };

  const removeGroup = async (groupId) => {
    if (!confirm("Retirer ce groupe de l'observation Bailey ?")) return;
    
    try {
      const res = await fetch(`${API_URL}/api/bailey/groups/${groupId}`, {
        method: "DELETE"
      });
      
      if (res.ok) {
        fetchData();
      }
    } catch (error) {
      console.error("Erreur suppression groupe:", error);
    }
  };

  // =============================================================================
  // RENDU - ÉTAT DE CHARGEMENT
  // =============================================================================

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="bailey-loading">
        <Loader2 className="w-8 h-8 animate-spin text-violet-400" />
      </div>
    );
  }

  // Groupes disponibles non encore observés
  const unobservedGroups = availableGroups.filter(
    ag => !observedGroups.find(og => og.group_id === ag.id)
  );

  // Activités en attente de validation (pour mise en évidence)
  const pendingActivities = activities.filter(a => a.status === "action_proposed");

  // =============================================================================
  // RENDU PRINCIPAL
  // =============================================================================

  return (
    <div className="space-y-6" data-testid="bailey-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-violet-500/20">
            <Bot className="w-6 h-6 text-violet-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Bailey</h1>
            <p className="text-zinc-400">Activité WhatsApp</p>
          </div>
        </div>
        
        {/* Navigation vue */}
        <div className="flex items-center gap-2">
          <Button
            variant={activeView === "activity" ? "default" : "outline"}
            size="sm"
            onClick={() => setActiveView("activity")}
            className={activeView === "activity" ? "bg-violet-600" : "border-zinc-700"}
          >
            <Eye className="w-4 h-4 mr-2" />
            Activité
          </Button>
          <Button
            variant={activeView === "config" ? "default" : "outline"}
            size="sm"
            onClick={() => setActiveView("config")}
            className={activeView === "config" ? "bg-violet-600" : "border-zinc-700"}
          >
            <Settings2 className="w-4 h-4 mr-2" />
            Groupes
          </Button>
        </div>
      </div>

      {/* Statut WhatsApp */}
      {!whatsappStatus.connected && (
        <Card className="bg-amber-500/5 border-amber-500/20">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-amber-400" />
              <div>
                <span className="text-amber-400 font-medium">WhatsApp non connecté</span>
                <p className="text-sm text-zinc-400">
                  Cliquez sur Reconnecter pour relancer le service
                </p>
              </div>
            </div>
            <Button
              onClick={handleReconnectWhatsApp}
              disabled={reconnecting}
              className="bg-amber-600 hover:bg-amber-700"
              size="sm"
            >
              {reconnecting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              {reconnecting ? "Connexion..." : "Reconnecter"}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Compteurs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4" data-testid="bailey-stats">
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-bold text-white">{stats.messages_seen}</div>
            <div className="text-sm text-zinc-400">Messages vus</div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-bold text-emerald-400">{stats.actions_auto + stats.actions_executed}</div>
            <div className="text-sm text-zinc-400">Actions auto</div>
          </CardContent>
        </Card>
        <Card className={`border ${stats.pending_validation > 0 ? "bg-violet-500/10 border-violet-500/30" : "bg-zinc-900/50 border-zinc-800"}`}>
          <CardContent className="p-4 text-center">
            <div className={`text-3xl font-bold ${stats.pending_validation > 0 ? "text-violet-400" : "text-white"}`}>
              {stats.pending_validation}
            </div>
            <div className="text-sm text-zinc-400">À valider</div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-bold text-zinc-500">{stats.ignored}</div>
            <div className="text-sm text-zinc-400">Ignorés</div>
          </CardContent>
        </Card>
      </div>

      {/* VUE ACTIVITÉ */}
      {activeView === "activity" && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-white">Journal d&apos;activité</h2>
          
          {activities.length === 0 ? (
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="p-8 text-center">
                <Clock className="w-12 h-12 mx-auto mb-4 text-zinc-600" />
                <p className="text-zinc-400">
                  En attente de messages WhatsApp
                </p>
                <p className="text-sm text-zinc-500 mt-2">
                  Les messages des groupes observés apparaîtront ici
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3" data-testid="activity-list">
              {/* Activités en attente de validation en premier */}
              {pendingActivities.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-violet-400 mb-3 flex items-center gap-2">
                    <CircleDot className="w-4 h-4" />
                    Actions en attente de validation ({pendingActivities.length})
                  </h3>
                  <div className="space-y-3">
                    {pendingActivities.map(activity => (
                      <ActivityCard
                        key={activity.id}
                        activity={activity}
                        onValidate={handleValidate}
                        onIgnore={handleIgnore}
                        onArchive={handleArchive}
                        onSendEmail={handleSendEmail}
                        onDelete={handleDelete}
                        isValidating={validatingId === activity.id}
                      />
                    ))}
                  </div>
                </div>
              )}
              
              {/* Toutes les activités */}
              <h3 className="text-sm font-medium text-zinc-400 mb-3">
                Historique récent
              </h3>
              {activities.filter(a => a.status !== "action_proposed").map(activity => (
                <ActivityCard
                  key={activity.id}
                  activity={activity}
                  onValidate={handleValidate}
                  onIgnore={handleIgnore}
                  onArchive={handleArchive}
                  onSendEmail={handleSendEmail}
                  onDelete={handleDelete}
                  isValidating={validatingId === activity.id}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* VUE CONFIGURATION GROUPES */}
      {activeView === "config" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">Groupes observés</h2>
            {unobservedGroups.length > 0 && (
              <Button 
                size="sm" 
                onClick={() => setShowAddGroup(true)}
                className="bg-violet-600 hover:bg-violet-700"
                data-testid="add-group-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Ajouter un groupe
              </Button>
            )}
          </div>

          {/* Info : groupes disponibles */}
          {whatsappStatus.connected && (
            <div className="text-sm text-zinc-400">
              {availableGroups.length} groupes WhatsApp détectés • {observedGroups.length} observés
            </div>
          )}

          {observedGroups.length === 0 ? (
            <Card className="bg-zinc-900/50 border-zinc-800 border-dashed">
              <CardContent className="p-8 text-center">
                <MessageSquare className="w-12 h-12 mx-auto mb-4 text-zinc-600" />
                <p className="text-zinc-400 mb-4">
                  Aucun groupe observé
                </p>
                {unobservedGroups.length > 0 && (
                  <Button 
                    onClick={() => setShowAddGroup(true)}
                    className="bg-violet-600 hover:bg-violet-700"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Choisir un groupe à observer
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3" data-testid="observed-groups-list">
              {observedGroups.map(group => (
                <Card 
                  key={group.group_id}
                  className="bg-zinc-900/50 border-zinc-800 hover:border-zinc-700 transition-colors"
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <div className="p-2 rounded-lg bg-zinc-800">
                        {group.group_type === "depense" ? (
                          <Mail className="w-5 h-5 text-amber-400" />
                        ) : (
                          <FolderOpen className="w-5 h-5 text-blue-400" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-white">{group.group_name}</span>
                          {group.group_type === "depense" && (
                            <Badge className="bg-amber-500/20 text-amber-400 text-xs">Dépenses</Badge>
                          )}
                          {group.group_type === "operationnel" && (
                            <Badge className="bg-blue-500/20 text-blue-400 text-xs">Documents</Badge>
                          )}
                        </div>
                        <p className="text-sm text-zinc-400 truncate">
                          {group.group_type === "depense" && group.expense_email 
                            ? `→ ${group.expense_email}`
                            : "→ Archivage Zoho"}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => {
                            setGroupConfig({
                              behavior: group.group_type || "depense",
                              email: group.expense_email || ""
                            });
                            setShowConfigGroup(group);
                          }}
                          className="text-zinc-400 hover:text-white"
                        >
                          <Settings2 className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => removeGroup(group.group_id)}
                          className="text-zinc-400 hover:text-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Dialog : Ajouter un groupe */}
      <Dialog open={showAddGroup} onOpenChange={setShowAddGroup}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-white">Choisir un groupe à observer</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Bailey surveillera ce groupe WhatsApp et traitera ses messages
            </DialogDescription>
          </DialogHeader>
          
          <div className="max-h-[400px] overflow-y-auto space-y-2">
            {unobservedGroups.length === 0 ? (
              <p className="text-center text-zinc-500 py-8">
                Tous vos groupes sont déjà observés
              </p>
            ) : (
              unobservedGroups.map(group => (
                <div 
                  key={group.id}
                  className="flex items-center gap-3 p-3 rounded-lg bg-zinc-800/50 hover:bg-zinc-800 cursor-pointer transition-colors"
                  onClick={() => addGroupToObserve(group)}
                  data-testid={`available-group-${group.id}`}
                >
                  <MessageSquare className="w-5 h-5 text-zinc-400" />
                  <span className="text-white flex-1">{group.name}</span>
                  <Plus className="w-4 h-4 text-zinc-500" />
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog : Configurer un groupe */}
      <Dialog open={!!showConfigGroup} onOpenChange={() => setShowConfigGroup(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-white">
              Configurer : {showConfigGroup?.group_name}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Comment Bailey doit-il traiter les messages de ce groupe ?
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Option Dépenses / Pennylane — 🔴 ETI-113-B : intégration ACTIVE seulement */}
            {hasIntegrationPennylane() && (
            <label 
              className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${
                groupConfig.behavior === "depense" 
                  ? "border-amber-500/50 bg-amber-500/10" 
                  : "border-zinc-700 hover:border-zinc-600"
              }`}
            >
              <input 
                type="radio" 
                name="behavior" 
                value="depense"
                checked={groupConfig.behavior === "depense"}
                onChange={() => setGroupConfig(c => ({ ...c, behavior: "depense" }))}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-amber-400" />
                  <span className="font-medium text-white">Tickets / Factures</span>
                </div>
                <p className="text-sm text-zinc-400 mt-1">
                  Images → Pièce jointe envoyée à Pennylane (OCR automatique)
                </p>
                
                {groupConfig.behavior === "depense" && (
                  <div className="mt-3">
                    <Label className="text-zinc-400 text-sm">Email Pennylane</Label>
                    <Input 
                      type="email"
                      placeholder="achats@entreprise.pennylane.com"
                      value={groupConfig.email}
                      onChange={e => setGroupConfig(c => ({ ...c, email: e.target.value }))}
                      className="mt-1 bg-zinc-800 border-zinc-700"
                    />
                  </div>
                )}
              </div>
            </label>
            )}

            {/* Option Planning / RDV */}
            <label 
              className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${
                groupConfig.behavior === "planning" 
                  ? "border-green-500/50 bg-green-500/10" 
                  : "border-zinc-700 hover:border-zinc-600"
              }`}
            >
              <input 
                type="radio" 
                name="behavior" 
                value="planning"
                checked={groupConfig.behavior === "planning"}
                onChange={() => setGroupConfig(c => ({ ...c, behavior: "planning" }))}
                className="mt-1"
              />
              <div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-green-400" />
                  <span className="font-medium text-white">Planning / RDV</span>
                </div>
                <p className="text-sm text-zinc-400 mt-1">
                  Messages analysés → Création automatique de RDV au calendrier
                </p>
              </div>
            </label>

            {/* Option Documents */}
            <label 
              className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${
                groupConfig.behavior === "operationnel" 
                  ? "border-blue-500/50 bg-blue-500/10" 
                  : "border-zinc-700 hover:border-zinc-600"
              }`}
            >
              <input 
                type="radio" 
                name="behavior" 
                value="operationnel"
                checked={groupConfig.behavior === "operationnel"}
                onChange={() => setGroupConfig(c => ({ ...c, behavior: "operationnel" }))}
                className="mt-1"
              />
              <div>
                <div className="flex items-center gap-2">
                  <FolderOpen className="w-4 h-4 text-blue-400" />
                  <span className="font-medium text-white">Documents / Photos</span>
                </div>
                <p className="text-sm text-zinc-400 mt-1">
                  Photos de chantier, documents → Archivage Zoho
                </p>
              </div>
            </label>

            {/* Option Ignorer */}
            <label 
              className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${
                groupConfig.behavior === "ignorer" 
                  ? "border-zinc-500/50 bg-zinc-500/10" 
                  : "border-zinc-700 hover:border-zinc-600"
              }`}
            >
              <input 
                type="radio" 
                name="behavior" 
                value="ignorer"
                checked={groupConfig.behavior === "ignorer"}
                onChange={() => setGroupConfig(c => ({ ...c, behavior: "ignorer" }))}
                className="mt-1"
              />
              <div>
                <div className="flex items-center gap-2">
                  <XCircle className="w-4 h-4 text-zinc-400" />
                  <span className="font-medium text-white">Ignorer</span>
                </div>
                <p className="text-sm text-zinc-400 mt-1">
                  Bailey n&apos;agit pas sur ce groupe
                </p>
              </div>
            </label>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowConfigGroup(null)}
              className="border-zinc-700"
            >
              Annuler
            </Button>
            <Button 
              onClick={saveGroupConfig}
              disabled={groupConfig.behavior === "depense" && !groupConfig.email}
              className="bg-violet-600 hover:bg-violet-700"
            >
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BaileyDashboard;
